import { currentLanguage, translate as tr } from "./i18n";
import type { ApiErrorPayload } from "./types";

const API_ROOT = "/api/v1";

export class ApiError extends Error {
  status: number;
  payload: ApiErrorPayload;

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message || tr("Ошибка запроса"));
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

function getCookie(name: string): string {
  const prefix = `${name}=`;
  const item = document.cookie.split(";").map((part) => part.trim()).find((part) => part.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : "";
}

async function parseError(response: Response): Promise<ApiErrorPayload> {
  try {
    return await response.json();
  } catch {
    return { code: "network_error", message: tr("Сервер вернул некорректный ответ."), field_errors: {}, trace_id: response.headers.get("X-Trace-Id") ?? "" };
  }
}

let refreshPromise: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_ROOT}/auth/refresh/`, {
      method: "POST",
      credentials: "include",
      headers: { "Accept-Language": currentLanguage(), "X-CSRFToken": getCookie("csrftoken") },
    }).then((response) => response.ok).finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

export async function api<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has("Accept-Language")) headers.set("Accept-Language", currentLanguage());
  if (!(init.body instanceof FormData) && init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (init.method && !["GET", "HEAD", "OPTIONS"].includes(init.method.toUpperCase())) headers.set("X-CSRFToken", getCookie("csrftoken"));
  const response = await fetch(`${API_ROOT}${path}`, { ...init, headers, credentials: "include" });
  const canRefresh = !path.startsWith("/auth/") || path === "/auth/me/";
  if (response.status === 401 && retry && canRefresh) {
    if (await refreshSession()) return api<T>(path, init, false);
  }
  if (!response.ok) throw new ApiError(response.status, await parseError(response));
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function ensureCsrf(): Promise<void> {
  await api<{ csrf_token: string }>("/auth/csrf/", {}, false);
}

export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  });
  const value = search.toString();
  return value ? `?${value}` : "";
}

export function humanError(error: unknown): string {
  if (error instanceof ApiError) return tr(error.payload.message) + (error.payload.trace_id ? ` · ${error.payload.trace_id}` : "");
  if (error instanceof Error) return tr(error.message);
  return tr("Не удалось выполнить действие.");
}
